import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api.service';

type Pricing = { title?: string; totalSteps: number; price: number; userPrice?: number; sortOrder?: number };
type DiscountTier = { _id?: string; title?: string; minTotalDeposited: number; discountPercent: number };

const AdminOrderPricingPage: React.FC = () => {
    const [pricing, setPricing] = useState<Pricing[]>([]);
    const [drafts, setDrafts] = useState<Record<number, string>>({});
    const [userPriceDrafts, setUserPriceDrafts] = useState<Record<number, string>>({});
    const [stepDrafts, setStepDrafts] = useState<Record<number, string>>({});
    const [titleDrafts, setTitleDrafts] = useState<Record<number, string>>({});
    const [sortOrderDrafts, setSortOrderDrafts] = useState<Record<number, string>>({});
    const [newTitle, setNewTitle] = useState('');
    const [newSteps, setNewSteps] = useState('');
    const [newPrice, setNewPrice] = useState('');
    const [newUserPrice, setNewUserPrice] = useState('');
    const [newSortOrder, setNewSortOrder] = useState('');
    const [discountTiers, setDiscountTiers] = useState<DiscountTier[]>([]);
    const [discountDrafts, setDiscountDrafts] = useState<Record<number, string>>({});
    const [minDepositedDrafts, setMinDepositedDrafts] = useState<Record<number, string>>({});
    const [discountTitleDrafts, setDiscountTitleDrafts] = useState<Record<number, string>>({});
    const [newMinDeposited, setNewMinDeposited] = useState('');
    const [newDiscountPercent, setNewDiscountPercent] = useState('');
    const [newDiscountTitle, setNewDiscountTitle] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const loadPricing = async () => {
        try {
            const data = await apiService.get<Pricing[]>('/orders/pricing');
            setPricing(data || []);
            setDrafts(Object.fromEntries((data || []).map((item) => [item.totalSteps, String(item.price)])));
            setUserPriceDrafts(Object.fromEntries((data || []).map((item) => [item.totalSteps, String(item.userPrice ?? item.price)])));
            setStepDrafts(Object.fromEntries((data || []).map((item) => [item.totalSteps, String(item.totalSteps)])));
            setTitleDrafts(Object.fromEntries((data || []).map((item) => [item.totalSteps, item.title || ''])));
            setSortOrderDrafts(Object.fromEntries((data || []).map((item) => [item.totalSteps, item.sortOrder ? String(item.sortOrder) : ''])));
        } catch {
            setError('Không tải được bảng giá.');
        }
    };

    const loadDiscountTiers = async () => {
        try {
            const data = await apiService.get<DiscountTier[]>('/transactions/discount-tiers');
            setDiscountTiers(data || []);
            setDiscountDrafts(Object.fromEntries((data || []).map((item) => [item.minTotalDeposited, String(item.discountPercent)])));
            setMinDepositedDrafts(Object.fromEntries((data || []).map((item) => [item.minTotalDeposited, String(item.minTotalDeposited)])));
            setDiscountTitleDrafts(Object.fromEntries((data || []).map((item, index) => [item.minTotalDeposited, item.title || `VIP ${index + 1}`])));
        } catch {
            setError('Không tải được cấu hình giảm giá.');
        }
    };

    useEffect(() => {
        void loadPricing();
        void loadDiscountTiers();
    }, []);

    const savePrice = async (
        currentSteps: number,
        steps: number,
        title: string,
        value: string,
        userPriceValue: string,
        sortOrder: string,
    ) => {
        const price = Number(value);
        const userPrice = Number(userPriceValue);
        if (!Number.isInteger(steps) || steps < 1) {
            setError('Số lần phải là số nguyên dương.');
            return;
        }
        if (!Number.isFinite(price) || price < 0) {
            setError('Giá phải là số không âm.');
            return;
        }
        if (!Number.isFinite(userPrice) || userPrice < 0) {
            setError('Giá User phải là số không âm.');
            return;
        }
        const parsedSortOrder = sortOrder.trim() ? Number(sortOrder) : null;
        if (parsedSortOrder !== null && (!Number.isInteger(parsedSortOrder) || parsedSortOrder < 1)) {
            setError('STT phải là số nguyên dương hoặc để trống.');
            return;
        }
        try {
            await apiService.post(`/orders/pricing/${currentSteps}`, {
                totalSteps: steps,
                title: title.trim(),
                price,
                userPrice,
                sortOrder: parsedSortOrder,
            });
            setMessage(title.trim() ? `Đã lưu ${title.trim()} (${steps} lần).` : `Đã lưu giá cho ${steps} lần.`);
            setError('');
            await loadPricing();
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không lưu được giá.');
        }
    };

    const addPrice = async () => {
        const steps = Number(newSteps);
        if (!Number.isInteger(steps) || steps < 1) {
            setError('Số lần phải là số nguyên dương.');
            return;
        }
        await savePrice(steps, steps, newTitle, newPrice, newUserPrice, newSortOrder);
        setNewTitle('');
        setNewSteps('');
        setNewPrice('');
        setNewUserPrice('');
        setNewSortOrder('');
    };

    const saveDiscountTier = async (previousMinTotalDeposited: number, minTotalDeposited: number, title: string, value: string) => {
        if (!Number.isFinite(minTotalDeposited) || minTotalDeposited < 0) {
            setError('Mốc tổng nạp phải là số không âm.');
            return;
        }
        const discountPercent = Number(value);
        if (!title.trim()) {
            setError('Tên khuyến mãi không được để trống.');
            return;
        }
        if (!Number.isFinite(discountPercent) || discountPercent < 0 || discountPercent > 100) {
            setError('Phần trăm giảm phải từ 0 đến 100.');
            return;
        }
        try {
            await apiService.post('/transactions/discount-tiers', { title: title.trim(), previousMinTotalDeposited, minTotalDeposited, discountPercent });
            setMessage(`Đã lưu ${title.trim()} (${discountPercent}%) từ ${minTotalDeposited.toLocaleString('vi-VN')} Like tổng nạp.`);
            setError('');
            await loadDiscountTiers();
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không lưu được cấu hình giảm giá.');
        }
    };

    const addDiscountTier = async () => {
        const minTotalDeposited = Number(newMinDeposited);
        if (!newDiscountTitle.trim()) {
            setError('Vui lòng nhập tên khuyến mãi.');
            return;
        }
        if (!Number.isFinite(minTotalDeposited) || minTotalDeposited < 0) {
            setError('Mốc tổng nạp phải là số không âm.');
            return;
        }
        await saveDiscountTier(minTotalDeposited, minTotalDeposited, newDiscountTitle, newDiscountPercent);
        setNewDiscountTitle('');
        setNewMinDeposited('');
        setNewDiscountPercent('');
    };

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50 p-4 md:p-8">
            <div className="mx-auto max-w-3xl space-y-5">
                <div><p className="text-sm font-medium text-blue-600">Cấu hình đơn hàng</p><h2 className="mt-1 text-2xl font-semibold text-gray-900">Giá theo số lần</h2><p className="mt-1 text-sm text-gray-500">Thành viên sẽ bị trừ tiền khi tạo đơn hàng; đối tác được ghi nhận công nợ.</p></div>
                {(message || error) && <div className={`rounded-xl px-4 py-3 text-sm ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>{error || message}</div>}
                <section className="rounded-2xl bg-white p-5 shadow-sm">
                    <div className="hidden grid-cols-[minmax(120px,1fr)_65px_65px_minmax(100px,0.8fr)_minmax(100px,0.8fr)_auto] gap-3 border-b border-gray-100 pb-3 text-xs font-semibold uppercase text-gray-500 sm:grid"><span>Tên gói</span><span>Số lần</span><span>STT</span><span>Giá Partner/Admin</span><span>Giá User</span><span /></div>
                    <div className="divide-y divide-gray-100">
                        {pricing.map((item) => <div key={item.totalSteps} className="grid grid-cols-1 items-center gap-2 py-3 sm:grid-cols-[minmax(120px,1fr)_65px_65px_minmax(100px,0.8fr)_minmax(100px,0.8fr)_auto] sm:gap-3"><input aria-label="Tên gói giá" value={titleDrafts[item.totalSteps] || ''} onChange={(event) => setTitleDrafts((current) => ({ ...current, [item.totalSteps]: event.target.value }))} className="min-w-0 rounded-lg border border-gray-200 px-3 py-2" placeholder="Tên gói (không bắt buộc)" /><input aria-label="Số lần" value={stepDrafts[item.totalSteps] || ''} onChange={(event) => setStepDrafts((current) => ({ ...current, [item.totalSteps]: event.target.value.replace(/\D/g, '') }))} className="min-w-0 rounded-lg border border-gray-200 px-3 py-2" inputMode="numeric" /><input aria-label="STT ưu tiên (không bắt buộc)" value={sortOrderDrafts[item.totalSteps] || ''} onChange={(event) => setSortOrderDrafts((current) => ({ ...current, [item.totalSteps]: event.target.value.replace(/\D/g, '') }))} className="min-w-0 rounded-lg border border-gray-200 px-3 py-2" inputMode="numeric" placeholder="-" /><input aria-label="Giá Partner/Admin" value={drafts[item.totalSteps] || ''} onChange={(event) => setDrafts((current) => ({ ...current, [item.totalSteps]: event.target.value.replace(/\D/g, '') }))} className="min-w-0 rounded-lg border border-gray-200 px-3 py-2" inputMode="numeric" /><input aria-label="Giá User" value={userPriceDrafts[item.totalSteps] || ''} onChange={(event) => setUserPriceDrafts((current) => ({ ...current, [item.totalSteps]: event.target.value.replace(/\D/g, '') }))} className="min-w-0 rounded-lg border border-gray-200 px-3 py-2" inputMode="numeric" /><button type="button" onClick={() => void savePrice(item.totalSteps, Number(stepDrafts[item.totalSteps]), titleDrafts[item.totalSteps] || '', drafts[item.totalSteps] || '0', userPriceDrafts[item.totalSteps] || '0', sortOrderDrafts[item.totalSteps] || '')} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700">Lưu</button></div>)}
                    </div>
                    {pricing.length === 0 && <p className="py-5 text-sm text-gray-500">Chưa có bảng giá.</p>}
                </section>
                <section className="rounded-2xl bg-white p-5 shadow-sm"><h3 className="font-semibold">Thêm giá mới</h3><div className="mt-3 flex flex-col gap-3 sm:flex-row"><input value={newTitle} onChange={(event) => setNewTitle(event.target.value)} placeholder="Tên riêng (không bắt buộc)" className="min-w-0 rounded-lg border px-3 py-2 sm:flex-1" /><input value={newSteps} onChange={(event) => setNewSteps(event.target.value.replace(/\D/g, ''))} placeholder="Số lần" className="rounded-lg border px-3 py-2 sm:w-32" inputMode="numeric" /><input value={newSortOrder} onChange={(event) => setNewSortOrder(event.target.value.replace(/\D/g, ''))} placeholder="STT (không bắt buộc)" aria-label="STT ưu tiên (không bắt buộc)" className="rounded-lg border px-3 py-2 sm:w-36" inputMode="numeric" /><input value={newPrice} onChange={(event) => setNewPrice(event.target.value.replace(/\D/g, ''))} placeholder="Giá Partner/Admin" aria-label="Giá Partner/Admin" className="rounded-lg border px-3 py-2 sm:w-36" inputMode="numeric" /><input value={newUserPrice} onChange={(event) => setNewUserPrice(event.target.value.replace(/\D/g, ''))} placeholder="Giá User" aria-label="Giá User" className="rounded-lg border px-3 py-2 sm:w-36" inputMode="numeric" /><button type="button" onClick={() => void addPrice()} className="rounded-lg bg-green-600 px-4 py-2 font-medium text-white hover:bg-green-700">Thêm giá</button></div></section>
                <section className="rounded-2xl bg-white p-5 shadow-sm">
                    <div>
                        <h3 className="font-semibold">Khuyến mãi theo tổng tiền đã nạp</h3>
                        <p className="mt-1 text-sm text-gray-500">Đặt tên cho từng hạng. Hệ thống áp dụng hạng cao nhất đạt được; người dùng được cộng tiền vào ví, đối tác được giảm tiền thanh toán đơn hàng.</p>
                    </div>
                    <div className="mt-4 hidden grid-cols-[minmax(120px,1fr)_minmax(120px,1fr)_minmax(120px,1fr)_auto] gap-3 border-b border-gray-100 pb-3 text-xs font-semibold uppercase text-gray-500 sm:grid">
                        <span>Tên hạng</span><span>Tổng nạp từ (Like)</span><span>Tỷ lệ (%)</span><span />
                    </div>
                    <div className="divide-y divide-gray-100">
                        {discountTiers.map((item) => (
                            <div key={item.minTotalDeposited} className="grid grid-cols-1 items-center gap-2 py-3 sm:grid-cols-[minmax(120px,1fr)_minmax(120px,1fr)_minmax(120px,1fr)_auto] sm:gap-3">
                                <input aria-label="Tên hạng khuyến mãi" value={discountTitleDrafts[item.minTotalDeposited] || ''} onChange={(event) => setDiscountTitleDrafts((current) => ({ ...current, [item.minTotalDeposited]: event.target.value }))} className="min-w-0 rounded-lg border border-gray-200 px-3 py-2" placeholder="Ví dụ: VIP 1" />
                                <input aria-label="Mốc tổng nạp" value={minDepositedDrafts[item.minTotalDeposited] || ''} onChange={(event) => setMinDepositedDrafts((current) => ({ ...current, [item.minTotalDeposited]: event.target.value.replace(/\D/g, '') }))} className="min-w-0 rounded-lg border border-gray-200 px-3 py-2" inputMode="numeric" />
                                <input
                                    aria-label="Tỷ lệ khuyến mãi"
                                    value={discountDrafts[item.minTotalDeposited] || ''}
                                    onChange={(event) => setDiscountDrafts((current) => ({ ...current, [item.minTotalDeposited]: event.target.value.replace(/[^0-9.]/g, '') }))}
                                    className="rounded-lg border border-gray-200 px-3 py-2"
                                    inputMode="decimal"
                                />
                                <button type="button" onClick={() => void saveDiscountTier(item.minTotalDeposited, Number(minDepositedDrafts[item.minTotalDeposited]), discountTitleDrafts[item.minTotalDeposited] || '', discountDrafts[item.minTotalDeposited] || '0')} className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700">Lưu</button>
                            </div>
                        ))}
                    </div>
                    {discountTiers.length === 0 && <p className="py-4 text-sm text-gray-500">Chưa có hạng khuyến mãi.</p>}
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                        <input value={newDiscountTitle} onChange={(event) => setNewDiscountTitle(event.target.value)} placeholder="Tên hạng (ví dụ: VIP 1)" className="min-w-0 rounded-lg border px-3 py-2 sm:flex-1" />
                        <input value={newMinDeposited} onChange={(event) => setNewMinDeposited(event.target.value.replace(/\D/g, ''))} placeholder="Tổng nạp từ (Like)" className="rounded-lg border px-3 py-2 sm:flex-1" inputMode="numeric" />
                        <input value={newDiscountPercent} onChange={(event) => setNewDiscountPercent(event.target.value.replace(/[^0-9.]/g, ''))} placeholder="Tỷ lệ (%)" className="rounded-lg border px-3 py-2 sm:w-32" inputMode="decimal" />
                        <button type="button" onClick={() => void addDiscountTier()} className="rounded-lg bg-green-600 px-4 py-2 font-medium text-white hover:bg-green-700">Thêm hạng</button>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default AdminOrderPricingPage;
